// LocalStorage utility for managing app data

interface StorageData<T = any> {
  value: T;
  timestamp: number;
  expiry: number | null;
}

interface AuthData {
  token: string;
  type: string | null;
  timestamp: number;
}

interface StorageInfo {
  totalKeys: number;
  validKeys: number;
  expiredKeys: number;
  totalSize: number;
  totalSizeKB: string;
}

class StorageManager {
  private prefix = 'flirto_';
  private defaultExpiry = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

  // Set a value with optional expiry
  set<T>(key: string, value: T, expiry: number | null = null): boolean {
    try {
      const data: StorageData<T> = {
        value,
        timestamp: Date.now(),
        expiry: expiry ? Date.now() + expiry : null,
      };
      localStorage.setItem(this.prefix + key, JSON.stringify(data));
      return true;
    } catch (error) {
      return false;
    }
  }

  // Get a value, returns null if expired or not found
  get<T>(key: string): T | null {
    try {
      const item = localStorage.getItem(this.prefix + key);
      if (!item) return null;

      const data: StorageData<T> = JSON.parse(item);

      // Check if expired
      if (data.expiry && Date.now() > data.expiry) {
        this.remove(key);
        return null;
      }

      return data.value;
    } catch (error) {
      return null;
    }
  }

  // Remove a specific key
  remove(key: string): boolean {
    try {
      localStorage.removeItem(this.prefix + key);
      return true;
    } catch (error) {
      return false;
    }
  }

  // Check if a key exists and is not expired
  has(key: string): boolean {
    return this.get(key) !== null;
  }

  // Set multiple values at once
  setMultiple(items: Record<string, any>, expiry: number | null = null): Record<string, boolean> {
    const results: Record<string, boolean> = {};
    Object.entries(items).forEach(([key, value]) => {
      results[key] = this.set(key, value, expiry);
    });
    return results;
  }

  // Get multiple values at once
  getMultiple(keys: string[]): Record<string, any> {
    const results: Record<string, any> = {};
    keys.forEach((key) => {
      results[key] = this.get(key);
    });
    return results;
  }

  // Clear all expired items
  clearExpired(): number {
    try {
      const keys = Object.keys(localStorage);
      let clearedCount = 0;

      keys.forEach((key) => {
        if (key.startsWith(this.prefix)) {
          const item = localStorage.getItem(key);
          if (item) {
            try {
              const data: StorageData = JSON.parse(item);
              if (data.expiry && Date.now() > data.expiry) {
                localStorage.removeItem(key);
                clearedCount++;
              }
            } catch (error) {
              // Invalid JSON, remove it
              localStorage.removeItem(key);
              clearedCount++;
            }
          }
        }
      });

      return clearedCount;
    } catch (error) {
      return 0;
    }
  }

  // Clear all app data
  clearAll(): number {
    try {
      const keys = Object.keys(localStorage);
      let clearedCount = 0;

      keys.forEach((key) => {
        if (key.startsWith(this.prefix)) {
          localStorage.removeItem(key);
          clearedCount++;
        }
      });

      return clearedCount;
    } catch (error) {
      return 0;
    }
  }

  // Get storage usage info
  getStorageInfo(): StorageInfo | null {
    try {
      const keys = Object.keys(localStorage);
      const appKeys = keys.filter((key) => key.startsWith(this.prefix));

      let totalSize = 0;
      let expiredCount = 0;
      let validCount = 0;

      appKeys.forEach((key) => {
        const item = localStorage.getItem(key);
        if (item) {
          totalSize += item.length;
          try {
            const data: StorageData = JSON.parse(item);
            if (data.expiry && Date.now() > data.expiry) {
              expiredCount++;
            } else {
              validCount++;
            }
          } catch (error) {
            expiredCount++;
          }
        }
      });

      return {
        totalKeys: appKeys.length,
        validKeys: validCount,
        expiredKeys: expiredCount,
        totalSize: totalSize,
        totalSizeKB: (totalSize / 1024).toFixed(2),
      };
    } catch (error) {
      return null;
    }
  }

  // Set auth data (special method for authentication)
  setAuth(authToken: string, authType: string | null = null, expiry: number | null = null): void {
    const authData: AuthData = {
      token: authToken,
      type: authType,
      timestamp: Date.now(),
    };

    this.set('auth', authData, expiry);
    this.set('authToken', authToken, expiry);

    if (authType) {
      this.set('authType', authType, expiry);
    }
  }

  // Get auth data
  getAuth(): AuthData | null {
    return this.get<AuthData>('auth');
  }

  // Get auth token
  getAuthToken(): string | null {
    return this.get<string>('authToken');
  }

  // Get auth type
  getAuthType(): string | null {
    return this.get<string>('authType');
  }

  // Clear auth data
  clearAuth(): void {
    this.remove('auth');
    this.remove('authToken');
    this.remove('authType');
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return this.has('authToken');
  }
}

// Create singleton instance
const storageManager = new StorageManager();

// Export functions
export const setStorage = <T>(key: string, value: T, expiry?: number | null) =>
  storageManager.set(key, value, expiry);
export const getStorage = <T>(key: string): T | null => storageManager.get<T>(key);
export const removeStorage = (key: string) => storageManager.remove(key);
export const hasStorage = (key: string) => storageManager.has(key);
export const setMultipleStorage = (items: Record<string, any>, expiry?: number | null) =>
  storageManager.setMultiple(items, expiry);
export const getMultipleStorage = (keys: string[]) => storageManager.getMultiple(keys);
export const clearExpiredStorage = () => storageManager.clearExpired();
export const clearAllStorage = () => storageManager.clearAll();
export const getStorageInfo = () => storageManager.getStorageInfo();

// Auth-specific functions
export const setAuth = (authToken: string, authType?: string | null, expiry?: number | null) =>
  storageManager.setAuth(authToken, authType, expiry);
export const getAuth = () => storageManager.getAuth();
export const getAuthToken = () => storageManager.getAuthToken();
export const getAuthType = () => storageManager.getAuthType();
export const clearAuth = () => storageManager.clearAuth();
export const isAuthenticated = () => storageManager.isAuthenticated();

// Make available globally for debugging
declare global {
  interface Window {
    storageManager: StorageManager;
    setStorage: typeof setStorage;
    getStorage: typeof getStorage;
    removeStorage: typeof removeStorage;
    setAuth: typeof setAuth;
    getAuth: typeof getAuth;
    clearAuth: typeof clearAuth;
    isAuthenticated: typeof isAuthenticated;
  }
}

if (typeof window !== 'undefined') {
  window.storageManager = storageManager;
  window.setStorage = setStorage;
  window.getStorage = getStorage;
  window.removeStorage = removeStorage;
  window.setAuth = setAuth;
  window.getAuth = getAuth;
  window.clearAuth = clearAuth;
  window.isAuthenticated = isAuthenticated;
}
